import React, { useEffect, useState, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Field, reduxForm } from 'redux-form'
import { isEmpty, get, keys, values, includes, filter, size, findKey } from 'lodash'
import { toast } from 'react-toastify'
import { uuid } from 'uuidv4'
import classNames from 'classnames'
import smoothscroll from 'smoothscroll-polyfill'

import { formattedHeader } from '../../utils/sectionHeader'
import { setSectionsToSpy } from '../../utils/spyScrolling'
import { MENU_TYPES, SHOULD_VISIT_MENU_TYPES } from '../../utils/menuTypes'
import { PREFERENCES_MAP, shouldVisitDropdownOptions } from '../../utils/customerPreferences'
import Money from '../../utils/money'
import Validation from '../../utils/validations'

import CheckboxGroupItem from '../CheckboxGroupItem'
import DropdownMenu from '../DropdownMenu'
import { DialogModal } from '../DialogModal'
import Loader from '../Loader'
import Modal from '../Modal'
import Section from '../Section'
import Textbox from '../Textbox'
import ToggleButton from '../ToggleButton'

let EventSuiteDetails = ({
  createNoOptimisticCustomerOrderingPreferences,
  currentVenueUuid,
  customerId,
  customerPayments,
  event,
  eventId,
  formAltered,
  formFields,
  formValues,
  history,
  isCustomerLoading,
  isOrderingPreferencesLoading,
  isPaymentLoading,
  isPreOrdersLoading,
  isRevenueCentersLoading,
  isSalesEventLoading,
  loadCustomerPayments,
  loadAllCustomerOrderingPreferencesByEventIdByCustomerId,
  onFieldValueChange,
  onMount,
  orderingPreference,
  paymentSelections,
  preferredPaymentList,
  setCustomerPaymentAuthorizedSignatory,
  standId,
  suite,
}) => {
  customerPayments = customerPayments.filter(
    payment => payment.salesEventId === null || payment.salesEventId === eventId
  )

  const sectionsRef = useRef({})
  const topPageRef = useRef(null)

  const [touchedPaymentFields, setTouchedPaymentFields] = useState({})
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [editSignatoryValue, setEditSignatoryValue] = useState('')
  const [customerPaymentId, setCustomerPaymentId] = useState('')
  const [currentScrollPosition, setCurrentScrollPosition] = useState(0)
  const [startSectionPositions, setStartSectionPositions] = useState(null)
  const [currentScrolledPosition, setCurrentScrolledPosition] = useState('general')

  const isLoading =
    isPaymentLoading || isOrderingPreferencesLoading || isCustomerLoading || isPreOrdersLoading || isRevenueCentersLoading || isSalesEventLoading
  const dismissAllToasts = () =>  toast.dismiss()

  useEffect(() => {
    if (!isLoading && isEmpty(touchedPaymentFields)) {
      dismissAllToasts()
    }
  }, [isLoading, touchedPaymentFields])

  const modalTitle = `${suite.rvcName} - ${suite.customerName}`

  const goBackToEvent = () => history.goBack()

  const scrollToSection = sectionHeaderIndex => {
    smoothscroll.polyfill()
    const sectionToScroll = sectionsRef.current[sectionHeaderIndex - 1]

    if (sectionToScroll === null) return

    if (sectionHeaderIndex === 0) {
      topPageRef.current.scrollIntoView({ behavior: 'smooth' })
    } else {
      sectionToScroll.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const renderPreferenceHeaders = useCallback(() => {
    let preferenceHeaders = isEmpty(orderingPreference) ? null : keys(orderingPreference)
    preferenceHeaders = preferenceHeaders.filter((header) => header !== 'entitlements')

    return preferenceHeaders
  }, [orderingPreference])

  // it is neccessary for re-rendering useEffect below
  const sectionsLength = size(sectionsRef.current)

  useEffect(() => {
    let sectionsPositions = [0]

    for (const [key, value] of Object.entries(sectionsRef.current)) {
      sectionsPositions = [...sectionsPositions, value.getBoundingClientRect().top]
    }

    setStartSectionPositions(sectionsPositions)
  }, [sectionsLength])

  useEffect(() => {
    if (!isEmpty(startSectionPositions)) {
      const headers = renderPreferenceHeaders()
      const filteredHeaders = headers.filter(header => !['pos_notes', 'customer_notes'].includes(header))
      const sectionsToSpy = setSectionsToSpy(startSectionPositions, filteredHeaders, 50)

      const currentSection = findKey(sectionsToSpy, section => {
        return currentScrollPosition >= section.range[0] && currentScrollPosition <= section.range[1]
      })
      setCurrentScrolledPosition(currentSection)
    }

  }, [currentScrollPosition, renderPreferenceHeaders, sectionsLength, startSectionPositions])

  useEffect(() => {
    if (!isEmpty(currentVenueUuid)) {
      onMount(customerId, currentVenueUuid)
    }
  }, [onMount, customerId, currentVenueUuid])

  const handlePaymentChange = event => {
    setTouchedPaymentFields({
      ...touchedPaymentFields,
      [event.target.id]: keys(touchedPaymentFields).includes(event.target.id)
        ? !touchedPaymentFields[event.target.id]
        : true
    })
  }

  const disableUpdateButton = () => {
    const formFieldsFilteredPaymentFields = filter(formFields, formField => !includes(formField, '.payment'))
    const updatedFieldsExist = formFieldsFilteredPaymentFields.map(formAltered).includes(true)

    return !updatedFieldsExist && !values(touchedPaymentFields).includes(true)
  }

  const findPreference = (attribute) => {
    const preference = values(PREFERENCES_MAP).find(preference => preference.attributes.includes(attribute))

    return preference
  }

  const prepPreferencesParams = (preferenceKeys, objectWithUpdates) => {
    const nestedArray = preferenceKeys.map(key => {
      let params = objectWithUpdates[key]

      if (["gratuity", "payment"].includes(key)) params = values(objectWithUpdates[key]) // keys are menu types

      if (key !== "flags") return params

      // shouldVisit is menu-specific but other flags aren't; need to massage data
      let flags = []
      keys(MENU_TYPES).forEach((menuType) => {
        if (!isEmpty(objectWithUpdates[key][menuType])) {
          flags.push(objectWithUpdates[key][menuType]) // create menu-specific shouldVisit preference
          delete (params[menuType]) // delete menu subtype key from remaining updatable object
        }
      })

      if (!isEmpty(params)) flags.push(params) // create remaining non-menu-specific flags preference

      return flags // combine menu-specific and non-menu-specific flag preferences
    })

    return nestedArray.flat()
  }

  const getStandardFieldDecomposition = (field) => {
    let fieldDecomposition = field.split('.')

    return {
      menuType: fieldDecomposition[0],
      objectType: fieldDecomposition[1],
      objectId: fieldDecomposition[fieldDecomposition.length - 1]
    }
  }

  const onPaymentPreferenceChange = (event) => {
    handlePaymentChange(event)
    onFieldValueChange(event.target.id, event.target.checked)
  }

  const onSubmitPreferenceChanges = (event) => {
    event.preventDefault()

    const toUpdate = {
      discount: {},
      flags: {},
      gratuity: {},
      notes: {},
      receipt: {},
      payment: {}
    }

    const uuids = {
      salesEventUuid: eventId,
      standUuid: standId,
      customerUuid: customerId,
    }

    formFields.forEach(field => {
      if (!formAltered(field)) {
        return
      }

      let value = get(formValues, field)
      let attribute = field
      let menuType = null
      let objectType = null
      let isCompositeField = field.includes(".")
      // If this is a field with composite information, break it down appropriately.
      // Standard field ID composition shall be :
      //     type.header.identifier
      // TODO(mkramerl): Update the remainder of fields to support this protocol.
      if (isCompositeField) {
        let fieldDecomposition = getStandardFieldDecomposition(field)

        menuType = fieldDecomposition.menuType
        objectType = fieldDecomposition.objectType
        attribute = fieldDecomposition.objectId
      }

      if (attribute === "fridgeUnlocked") {
        attribute = "lockRefrigerator"
        value = !value
      } else if (attribute === "liquorCabinetUnlocked") {
        attribute = "lockLiquorCabinet"
        value = !value
      }

      const preferenceType = get(findPreference(attribute), 'type')
      const preferenceAttributes = get(findPreference(attribute), 'attributes')
      const preferenceMapKey = keys(PREFERENCES_MAP).find(key => PREFERENCES_MAP[key].type === preferenceType)

      const newUuid = uuid()
      const basePreferenceObject = {
        ...uuids,
        type: preferenceType,
        uuid: newUuid,
      }

      // menuType preferences can be determined by their compositeness : whether they have a menu
      // type header in the ID.
      if (isCompositeField) {
        const gratuity = get(formValues, field.replace(attribute, "gratuity"))
        const gratuityIsPercentage = String(get(formValues, field.replace(attribute, "gratuityIsPercentage"))) === "true"
        const maxGratuity = get(formValues, field.replace(attribute, "maxGratuity"))
        const menuPreferenceObject = { ...basePreferenceObject, menuSubtype: menuType }

        // Agglomerate the selected payments into a single array preference to be set for the event.
        if (objectType === "payment") {
          if (!paymentSelections[menuType]) {
            paymentSelections[menuType] = {}
          }

          if (value) {
            paymentSelections[menuType][attribute] = true
          } else {
            delete paymentSelections[menuType][attribute]
          }

          return
        }

        // shouldVisit
        if (attribute === "shouldVisit") {
          let visitValue = value === "true"

          if (value === "") visitValue = null

          toUpdate[preferenceMapKey][menuType] = { ...menuPreferenceObject, shouldVisit: visitValue }

          return
        }

        // gratuity
        if (toUpdate[preferenceMapKey][menuType]) {
          toUpdate[preferenceMapKey][menuType][attribute] = value
          toUpdate[preferenceMapKey][menuType]["gratuity"] = gratuityIsPercentage ? gratuity : Money.gratuityToInteger(gratuity)
          toUpdate[preferenceMapKey][menuType]["maxGratuity"] = Money.gratuityToInteger(maxGratuity)
        }

        toUpdate[preferenceMapKey][menuType] = { ...menuPreferenceObject }
        preferenceAttributes.forEach(attr => {
          const currentValue = get(formValues, field.replace(attribute, attr))

          toUpdate[preferenceMapKey][menuType][attr] = currentValue
        })

        toUpdate[preferenceMapKey][menuType][attribute] = value
        toUpdate[preferenceMapKey][menuType]["gratuity"] = gratuityIsPercentage ? gratuity : Money.gratuityToInteger(gratuity)
        toUpdate[preferenceMapKey][menuType]["maxGratuity"] = Money.gratuityToInteger(maxGratuity)

        return
      }

      // non-menuType preferences
      if (!isEmpty(toUpdate[preferenceMapKey])) {
        toUpdate[preferenceMapKey][attribute] = value

        return
      }

      toUpdate[preferenceMapKey] = { ...basePreferenceObject }

      preferenceAttributes.forEach(attr => {
        const currentValue = get(formValues, attr)

        toUpdate[preferenceMapKey][attr] = currentValue
      })

      toUpdate[preferenceMapKey][attribute] = value
    })

    const menuTypes = keys(orderingPreference)

    menuTypes.forEach(menuType => {
      if (!paymentSelections[menuType]) {
        return
      }

      // TODO(mkramerl): Right now salesEventUuid is also being sent in networking, but this
      //     isn't the property on API side.
      toUpdate.payment[menuType] = {
        uuid: uuid(),
        customerId: customerId,
        customerPaymentIds: keys(paymentSelections[menuType]),
        menuSubtype: menuType,
        salesEventId: eventId,
        standUuid: standId,
        type: "Customer::PaymentPreference",
      }
    })

    // filter toUpdate and only the ones that are not empty will be updated
    const preferenceKeys = keys(toUpdate).filter(key => !isEmpty(toUpdate[key]))
    const arrayOfPreferences = prepPreferencesParams(preferenceKeys, toUpdate)

    createNoOptimisticCustomerOrderingPreferences(customerId, arrayOfPreferences).then(() =>
      loadAllCustomerOrderingPreferencesByEventIdByCustomerId(eventId, customerId)
    )

    setTouchedPaymentFields({})
    toast.success('Update successful!', {
      onClose: () => {
        renderPreferenceHeaders().map((preferenceHeader, key) => {
          return preferenceHeader === currentScrolledPosition && scrollToSection(key)
        })
      }
    })
  }

  if (isEmpty(suite)) return null

  const renderShouldVisitDropdown = (menuSubtype) => {
    let element = null

    if (SHOULD_VISIT_MENU_TYPES.includes(menuSubtype)) {
      element = (
        <Field
          className='-full-width -mb'
          component={DropdownMenu}
          includeEmpty={false}
          options={shouldVisitDropdownOptions()}
          name={`${menuSubtype}.shouldVisit`}
        />
      )
    }

    return element
  }

  const orderingPreferenceContent = (header) => {
    let className = ''
    switch (header) {
      case 'general':
        return (
          <div className='general'>
            <div className='content-wrapper'>
              <Field
                name='host'
                component={Textbox}
                label='Host'
              />
            </div>
            <div className='content-wrapper'>
              <Field
                name='receiptEmail'
                component={Textbox}
                label='Email(s)'
                validate={Validation.emailIsValid}
              />
            </div>
          </div>
        )

      case 'flags':
        const { discountDescription } = orderingPreference[header]

        return (
          <div className='flags'>
            <div className='content-wrapper -flags-wrapper'>
              <span className="content-header">Open Fridge</span>
              <span className="content-value">
                <Field name='fridgeUnlocked' component={ToggleButton} />
              </span>
            </div>
            <div className="content-wrapper -flags-wrapper">
              <span className="content-header">Open Liquor Cabinet</span>
              <span className="content-value">
                <Field name='liquorCabinetUnlocked' component={ToggleButton} />
              </span>
            </div>
            <div className='content-wrapper -flags-wrapper'>
              <span className="content-header">Present Bill</span>
              <span className="content-value">
                <Field name='presentBill' component={ToggleButton} />
              </span>
            </div>
            <div className='content-wrapper -flags-wrapper'>
              <span className="content-header">Print Receipt</span>
              <span className="content-value">
                <Field name='provideReceipt' component={ToggleButton} />
              </span>
            </div>
            <div className='content-wrapper -flags-wrapper'>
              <span className="content-header">Tax Exempt</span>
              <span className="content-value">
                <Field name='taxExempt' component={ToggleButton} />
              </span>
            </div>
            <div className='content-wrapper -flags-wrapper'>
              <span className="content-header">Discount</span>
              <span className="content-value text-bold">{discountDescription ?? ''}</span>
            </div>
          </div>
        )

      case 'entitlements':
        return null

      case 'customer_notes':
        return (
          <div className='notes'>
            <div className='content-wrapper'>
              <Field name='notes' component={Textbox} textarea={true} props={{ readonly: true }} />
            </div>
          </div>
        )

      case 'pos_notes':
        return (
          <div className='notes'>
            <div className='content-wrapper'>
              <Field name='posNotes' component={Textbox} label="POS Notes" textarea={true} className="-textareaPOS" />
            </div>
          </div>
        )

      default: // if it's a menutype
        className = header.includes("_") ? header.split("_").join("-") : header
        const gratuityTypeOptions = [
          { value: true, displayName: "Percent" },
          { value: false, displayName: "Dollar" }
        ]

        const gratuityAdjustableOptions = [
          { value: true, displayName: "Adjustable" },
          { value: '', displayName: "Non Adjustable" }
        ]

        const paymentOptions = customerPayments.map(payment => {
          const displayName = payment.paymentType === 24 ? 'Direct Bill' : payment.shortDescription
          const preferredPrefix = get(preferredPaymentList, header, []).includes(payment.uuid) ? '(Preferred) ' : ''
          const nickname = payment.nickname ? preferredPrefix + payment.nickname : preferredPrefix

          return { value: payment.uuid, displayName: displayName, signatory: payment.authorizedSignatory, nickname: nickname }
        })

        paymentSelections[header] = {}
        const paymentOptionSelections = paymentOptions.map((paymentOption, key) => {
          let paymentId = header + '.payment.' + paymentOption.value
          let selectedUuids = orderingPreference[header].payments.map((payment) => {
            return payment.uuid
          })
          let checked = selectedUuids.includes(paymentOption.value)

          if (checked) {
            paymentSelections[header][paymentOption.value] = true
          }

          const tooltipText = paymentOption.signatory || 'Add Signers'

          // TODO(mkramerl): Set the checkbox state on the basis of the existing event preferences.
          return <div className='content-row' key={key}>
            <div className="content-value">
              <Field
                name={paymentId}
                component={CheckboxGroupItem}
                defaultChecked={checked}
                textAlignLeft={true}
                props={{
                  tooltipText,
                  id: paymentId,
                  label: paymentOption?.displayName,
                  onChange: onPaymentPreferenceChange,
                  subtext: paymentOption?.nickname,
                  handleOpenModal: () => {
                    setCustomerPaymentId(paymentOption.value)
                    setIsEditModalOpen(true)
                    setEditSignatoryValue(paymentOption.signatory)
                  },
                  isEditModalOpen: isEditModalOpen
                }}
              />
            </div>
          </div>
        })

        return (
          <div className={className}>
            {renderShouldVisitDropdown(header)}
            <div className='content-table'>
              <div className='content-row -spread'>
                <div className="content-column">
                  <div className="content-header">Payment Options</div>
                </div>
                <div className="content-column">
                  <div className="content-header">Signatory</div>
                </div>
              </div>
              {paymentOptionSelections}
            </div>
            <div className="preference-header -subheader">
              Gratuity amount
            </div>
            <div className='content-wrapper'>
              <Field
                className='-inline-field'
                name={`${header}.gratuityIsPercentage`}
                component={DropdownMenu}
                options={gratuityTypeOptions}
              />
            <Field
              wrapperClassName="-inline-field"
              name={`${header}.gratuity`}
              component={Textbox}
              label="Amount"
              type="number"
              minValue='0'
            />
            </div>
            <div className='content-wrapper'>
              <Field
                className='-inline-field'
                name={`${header}.gratuityIsAdjustable`}
                component={DropdownMenu}
                options={gratuityAdjustableOptions}
              />
              <Field
                wrapperClassName='-inline-field'
                className='max-gratuity'
                name={`${header}.maxGratuity`}
                component={Textbox}
                label="Maximum Gratuity"
                type="number"
                minValue='0'
                required={true}
              />
            </div>
          </div>
        )
    }
  }

  const orderingPreferenceSection = () => {
    const sectionsWithoutHeader = ['entitlements']

    return (
      renderPreferenceHeaders().map((header, idx) => (
          <Section
            key={idx}
            className={`-${header}`}
          >
            {orderingPreference[header].showHeader && !sectionsWithoutHeader.includes(header) && (
              <div className="preference-header">
                {formattedHeader(header)}
              </div>
            )}
            {orderingPreferenceContent(header)}
            {<div ref={el => sectionsRef.current[idx] = el}></div>}
          </Section>
        )
      )
    )
  }

  const handleSignatoryChange = event => {
    setEditSignatoryValue(event.target.value)
  }

  const editModalBody = (
    <section className="signatory-modal-body">
      <div className="signatory-modal-body__wrapper">
        <Textbox
          label="List of signers"
          labelBottom="Comma separate multiple names"
          handleChange={handleSignatoryChange}
          value={editSignatoryValue}
          textarea
          className={'-textareaSignatory'}
        />
      </div>
    </section>
  )

  const dialogModalConfirmationCallback = () => {
    setCustomerPaymentAuthorizedSignatory({
      authorizedSignatory: editSignatoryValue,
      uuid: customerPaymentId
    })
    .then(() => loadCustomerPayments(customerId))
  }

  return (
    <>
      {isLoading ? <Loader /> : (
        <Modal
          className="-fluid"
          onClose={goBackToEvent}
          title={modalTitle}
          getScrollPosition={setCurrentScrollPosition}
        >
          <div ref={topPageRef}></div>
          <div className="modal-wrapper">
            <div className="suite" id='suite'>
              <form className='ordering-preference'>
                <fieldset disabled={event.locked}>
                  {orderingPreferenceSection()}
                  <div className="btn-wrapper">
                    <button
                      className={classNames('btn', '-update')}
                      onClick={onSubmitPreferenceChanges}
                      disabled={disableUpdateButton()}
                    >
                      Update
                    </button>
                  </div>
                </fieldset>
              </form>
            </div>
            <div className="modal-menu">
              <h1 className="modal-menu__header">Sections</h1>
              <nav className="modal-menu__list">
                <ul>
                  {renderPreferenceHeaders().map((preferenceHeader, key) => (
                    <li key={key}>
                      <button onClick={() => scrollToSection(key)} className={classNames({'active': preferenceHeader === currentScrolledPosition})}>
                        {formattedHeader(preferenceHeader)}
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
          </div>
          <DialogModal
            confirmationCallback={dialogModalConfirmationCallback}
            closeModalCallback={() => setIsEditModalOpen(false)}
            isCancelButton
            modalBody={editModalBody}
            modalButtonText="Save"
            modalIsOpen={isEditModalOpen}
            modalTitle="Signatory"
          />
        </Modal>
      )}
    </>
  )
}

EventSuiteDetails.defaultProps = {
  customerId: '',
  customerPayments: [],
  event: {},
  eventId: '',
  formAltered: false,
  formFields: [],
  formValues: [],
  history: {},
  isCustomerLoading: false,
  isPreOrdersLoading: false,
  isRevenueCentersLoading: false,
  isSalesEventLoading: false,
  orderingPreference: {},
  paymentSelections: {},
  preferredPaymentList: {},
  standId: '',
  suite: {},
  createNoOptimisticCustomerOrderingPreferences: () => { },
  onMount: () => { },
}

EventSuiteDetails.propTypes = {
  createNoOptimisticCustomerOrderingPreferences: PropTypes.func,
  customerId: PropTypes.string,
  customerPayments: PropTypes.arrayOf(PropTypes.object),
  event: PropTypes.object,
  eventId: PropTypes.string,
  formAltered: PropTypes.bool,
  formFields: PropTypes.array,
  formValues: PropTypes.object,
  history: PropTypes.object,
  isCustomerLoading: PropTypes.bool,
  isPreOrdersLoading: PropTypes.bool,
  isRevenueCentersLoading: PropTypes.bool,
  isSalesEventLoading: PropTypes.bool,
  onMount: PropTypes.func,
  orderingPreference: PropTypes.object,
  paymentSelections: PropTypes.object,
  preferredPaymentList: PropTypes.object,
  standId: PropTypes.string,
  suite: PropTypes.object,
}

EventSuiteDetails = reduxForm({
  form: 'ordering-preference',
  enableReinitialize: true
})(EventSuiteDetails)

export default EventSuiteDetails
